import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash, MagnifyingGlass, CurrencyDollar } from "@phosphor-icons/react";
import { formatBRL, formatDate } from "@/lib/format";
import { toast } from "sonner";

const Payments = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/payments"); setItems(data); }
    catch { toast.error("Erro"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [p.user_name, p.method, p.reference_period].filter(Boolean).some((f) => f.toLowerCase().includes(s));
  });
  const total = filtered.reduce((acc, p) => acc + Number(p.amount || 0), 0);

  const remove = async (p) => {
    if (!window.confirm("Excluir este pagamento?")) return;
    try { await api.delete(`/admin/payments/${p.id}`); load(); toast.success("Removido"); }
    catch { toast.error("Erro"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Histórico</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Pagamentos</h1>
        </div>
        <Card className="border-zinc-200 px-4 py-3 card-soft">
          <div className="text-[11px] tracking-widest uppercase text-zinc-500">Total filtrado</div>
          <div className="metric-num text-2xl text-emerald-700 mt-1">{formatBRL(total)}</div>
        </Card>
      </div>

      <div className="relative max-w-md mb-4">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16}/>
        <Input className="pl-9 h-10" placeholder="Buscar por cliente, método..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="payments-search"/>
      </div>

      <Card className="border-zinc-200 p-0 overflow-hidden card-soft hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/80 border-b border-zinc-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="px-5 py-3 font-semibold">Cliente</th>
                <th className="px-3 py-3 font-semibold">Valor</th>
                <th className="px-3 py-3 font-semibold">Pago em</th>
                <th className="px-3 py-3 font-semibold">Método</th>
                <th className="px-3 py-3 font-semibold">Referência</th>
                <th className="px-3 py-3 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">
                  <CurrencyDollar size={28} weight="duotone" className="mx-auto mb-2 text-zinc-400"/>
                  Nenhum pagamento registrado.
                </td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-5 py-3 font-medium">{p.user_name}</td>
                  <td className="px-3 py-3 tab-nums text-emerald-700 font-semibold">{formatBRL(p.amount)}</td>
                  <td className="px-3 py-3">{formatDate(p.paid_at)}</td>
                  <td className="px-3 py-3 capitalize">{p.method}</td>
                  <td className="px-3 py-3 text-zinc-500">{p.reference_period || "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => remove(p)} data-testid={`payment-delete-${p.id}`}><Trash size={16}/></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile list */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <Card className="border-zinc-200 p-8 text-center text-zinc-500 text-sm">
            <CurrencyDollar size={28} weight="duotone" className="mx-auto mb-2 text-zinc-400"/>
            Nenhum pagamento registrado.
          </Card>
        ) : filtered.map((p) => (
          <Card key={p.id} className="border-zinc-200 p-4 card-soft">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{p.user_name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{formatDate(p.paid_at)} · <span className="capitalize">{p.method}</span>{p.reference_period ? ` · ${p.reference_period}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="tab-nums text-emerald-700 font-semibold">{formatBRL(p.amount)}</div>
                <Button variant="ghost" size="icon" className="text-rose-600 -mr-2 -mb-1" onClick={() => remove(p)}><Trash size={16}/></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Payments;
